import React, { useCallback, useEffect, useState } from "react";
import { Image, Linking, StyleSheet } from "react-native";
import Loading from "../Loading";
import Pdf from "../Pdf";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import { getStorage, ref, getMetadata } from "../../contexts/FirebaseStorage";
import Text from "../Text";
import VStack from "../../ui_atoms/VStack";
import Icon from "../Icon";
import { BodyMediumText, BodySmallText } from "../FormattedText";
import { useI18n } from "../../hooks/i18n-hooks";
import { useTheme } from "../../hooks/use-theme";

const fetchDocumentUrl = async (url: string) => {
    const response = await fetch(url);
    const blob = await response.blob();

    return new Promise<{ base64: string; mimeType: string }>((onSuccess, onError) => {
        try {
            const reader = new FileReader();
            reader.onload = function () {
                onSuccess({
                    base64: (this.result as string).replace(/^data:.+;base64,/, ""),
                    mimeType: response.headers.get("content-type") as string,
                });
            };
            reader.readAsDataURL(blob);
        } catch (e) {
            onError(e instanceof Error ? e : new Error(String(e)));
        }
    });
};

// A type we cannot identify falls back to the "preview unavailable" state instead of loading forever.
const UNKNOWN_MIME_TYPE = "application/octet-stream";

// Content-Type values may carry parameters or odd casing ("application/pdf; charset=binary").
const normalizeMimeType = (mimeType: string | null | undefined) =>
    mimeType?.split(";")[0].trim().toLowerCase() || UNKNOWN_MIME_TYPE;

export type DocumentViewerProps = {
    documentUrl: string;
    knownMimeType?: string;
    tintColor?: string;
    // Shown under the "preview unavailable" message for types we cannot render (CSV, Word, …).
    fileName?: string;
};

const DocumentViewer: React.FC<DocumentViewerProps> = ({
    documentUrl,
    knownMimeType,
    tintColor,
    fileName,
}) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    // Keyed by URL so a type resolved for a previous document is never applied to the current one.
    const [resolvedMimeType, setResolvedMimeType] = useState<{
        documentUrl: string;
        mimeType: string;
    } | null>(null);
    const [error, setError] = useState<string | null>();
    const mimeType = knownMimeType
        ? normalizeMimeType(knownMimeType)
        : resolvedMimeType?.documentUrl === documentUrl
          ? resolvedMimeType.mimeType
          : null;

    const onError = useCallback((error: object) => {
        let errorMessage: string;
        if (error instanceof Error) {
            errorMessage = error.message;
        } else {
            errorMessage = JSON.stringify(error);
        }
        setError(errorMessage);
    }, []);

    useEffect(() => {
        if (knownMimeType) {
            return;
        }
        let cancelled = false;
        void (async () => {
            let contentType: string | null | undefined;
            try {
                const fileRef = ref(getStorage(), documentUrl);
                const metadata = await getMetadata(fileRef);
                contentType = metadata.contentType;
            } catch (_e) {
                try {
                    // fallback to fetch in worst case
                    const data = await fetchDocumentUrl(documentUrl);
                    contentType = data.mimeType;
                } catch (_e) {
                    contentType = null;
                }
            }
            if (!cancelled) {
                setResolvedMimeType({ documentUrl, mimeType: normalizeMimeType(contentType) });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [documentUrl, knownMimeType]);

    return (
        <LoadingBoundary
            error={error}
            clearError={() => {
                setError(null);
            }}
        >
            {mimeType ? (
                mimeType.split("/")[0] === "image" ? (
                    <Image
                        onError={onError}
                        resizeMode="contain"
                        style={{ flex: 1 }}
                        source={{ uri: documentUrl }}
                    />
                ) : mimeType === "application/pdf" ? (
                    <Pdf
                        trustAllCerts={false}
                        source={{
                            uri: documentUrl,
                        }}
                        renderActivityIndicator={(e) => (
                            <Text style={{ color: tintColor }}>{(e * 100).toFixed(0)}%</Text>
                        )}
                        onPressLink={(url) => {
                            void Linking.openURL(url);
                        }}
                        onError={onError}
                        style={{
                            flex: 1,
                            backgroundColor: "transparent",
                            width: "100%",
                        }}
                    />
                ) : (
                    <VStack alignment="center" gap={0.5} h={1} style={styles.previewUnavailable}>
                        <Icon name="file" size={48} color={colors.greyAccessible} />
                        <BodyMediumText style={styles.centeredText}>
                            {i18n.t("components.document.preview_unavailable")}
                        </BodyMediumText>
                        {fileName ? (
                            <BodySmallText
                                color={colors.greyAccessible}
                                numberOfLines={2}
                                style={styles.centeredText}
                            >
                                {fileName}
                            </BodySmallText>
                        ) : null}
                    </VStack>
                )
            ) : (
                <Loading />
            )}
        </LoadingBoundary>
    );
};

const styles = StyleSheet.create({
    previewUnavailable: {
        flex: 1,
        justifyContent: "center",
    },
    centeredText: {
        textAlign: "center",
    },
});

export default DocumentViewer;
